import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  getPublicGraduationDepositDetails,
  submitPublicGraduationDepositPayment,
  initiatePublicGraduationDepositCardPayment
} from '../../../../services/crmApi';
import LoadingSpinner from '../../../Common/LoadingSpinner';
import PublicGraduationPassPayment from './PublicGraduationPassPayment';

const PAYMENT_METHOD_LABELS = {
  BANK_TRANSFER: 'Bank Transfer',
  CHEQUE: 'Cheque',
  ONLINE_PAYMENT: 'Online Payment',
  MOBILE_PAYMENT: 'Mobile Payment',
  OTHER: 'Other',
  CARD: 'Card (Credit/Debit)',
  CASH: 'Cash'
};

const PUBLIC_PAYMENT_METHODS = ['BANK_TRANSFER', 'CHEQUE', 'ONLINE_PAYMENT', 'MOBILE_PAYMENT', 'OTHER', 'CARD'];

const PublicGraduationDepositPayment = () => {
  const { token } = useParams();
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    paymentMethod: '',
    referenceNumber: '',
    bankName: '',
    branchName: '',
    notes: ''
  });

  useEffect(() => {
    fetchDetails();
  }, [token]);

  const fetchDetails = async () => {
    try {
      setLoading(true);
      const response = await getPublicGraduationDepositDetails(token);
      const data = response.data.data;
      const paymentMethods = (data.paymentMethods || PUBLIC_PAYMENT_METHODS).filter((m) => m !== 'CASH');
      setDetails({ ...data, paymentMethods });
      if (paymentMethods.length) {
        setForm((f) => ({ ...f, paymentMethod: paymentMethods[0] }));
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load payment details');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.paymentMethod) return;
    try {
      setSubmitting(true);
      if (form.paymentMethod === 'CARD') {
        const response = await initiatePublicGraduationDepositCardPayment(token);
        const paymentPageUrl = response.data?.data?.paymentPageUrl;
        if (paymentPageUrl) {
          window.location.href = paymentPageUrl;
        } else {
          setError('Could not start card payment. Please try again.');
        }
      } else {
        await submitPublicGraduationDepositPayment(token, form);
        setSubmitted(true);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to submit payment');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <LoadingSpinner />;
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="bg-white rounded-xl shadow p-8 max-w-md w-full text-center">
          <h2 className="text-lg font-semibold text-red-600 mb-2">Payment Link Error</h2>
          <p className="text-gray-700">{error}</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="bg-white rounded-xl shadow p-8 max-w-md w-full text-center">
          <h2 className="text-lg font-semibold text-green-600 mb-2">Payment Submitted</h2>
          <p className="text-gray-700">Your deposit payment has been submitted and will be reviewed by staff.</p>
        </div>
      </div>
    );
  }

  if (details.passes) {
    return <PublicGraduationPassPayment token={token} details={details} />;
  }

  const amountFormatted = new Intl.NumberFormat('en-US', { style: 'currency', currency: details.currency }).format(details.amount);
  const dueFormatted = details.dueAmount != null
    ? new Intl.NumberFormat('en-US', { style: 'currency', currency: details.currency }).format(details.dueAmount)
    : null;
  const paidFormatted = details.paidAmount > 0
    ? new Intl.NumberFormat('en-US', { style: 'currency', currency: details.currency }).format(details.paidAmount)
    : null;

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto bg-white rounded-xl shadow-md overflow-hidden">
        <div className="bg-indigo-600 px-6 py-4">
          <h1 className="text-xl font-bold text-white">Graduation Deposit Payment</h1>
          <p className="text-indigo-100 text-sm">{details.graduationName}</p>
        </div>
        <div className="p-6 space-y-6">
          <div className="bg-indigo-50 rounded-xl p-4">
            <p className="text-sm text-gray-600">
              {details.depositStatus === 'PARTIALLY_PAID' ? 'Balance Due' : 'Deposit Amount'}
            </p>
            <p className="text-3xl font-bold text-indigo-900">{amountFormatted}</p>
            {details.depositStatus === 'PARTIALLY_PAID' && dueFormatted && paidFormatted && (
              <p className="text-sm text-gray-600 mt-2">
                Total due {dueFormatted} · Paid so far {paidFormatted}
              </p>
            )}
          </div>

          <div className="border border-gray-200 rounded-xl p-4">
            <h2 className="text-sm font-semibold text-gray-900 mb-2">Student Details</h2>
            <p className="text-sm text-gray-700">{details.student?.name}</p>
            <p className="text-sm text-gray-500">{details.student?.email}</p>
            <p className="text-sm text-gray-500">{details.student?.phone}</p>
          </div>

          {details.bankAccount?.accountNumber && (
            <div className="border border-gray-200 rounded-xl p-4">
              <h2 className="text-sm font-semibold text-gray-900 mb-2">Bank Account</h2>
              <p className="text-sm text-gray-700">{details.bankAccount.accountName}</p>
              <p className="text-sm text-gray-700">{details.bankAccount.accountNumber}</p>
              <p className="text-sm text-gray-500">{details.bankAccount.bankName} {details.bankAccount.branchName}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-800 mb-1">Payment Method</label>
              <select
                name="paymentMethod"
                value={form.paymentMethod}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                required
              >
                <option value="">Select method</option>
                {details.paymentMethods.map((method) => (
                  <option key={method} value={method}>{PAYMENT_METHOD_LABELS[method] || method}</option>
                ))}
              </select>
            </div>

            {form.paymentMethod !== 'CARD' && (
              <>
                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-1">Reference Number</label>
                  <input
                    name="referenceNumber"
                    value={form.referenceNumber}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Transaction / Cheque / Slip number"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-1">Bank Name</label>
                  <input
                    name="bankName"
                    value={form.bankName}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Bank name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-1">Branch Name</label>
                  <input
                    name="branchName"
                    value={form.branchName}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Branch name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-1">Notes</label>
                  <textarea
                    name="notes"
                    value={form.notes}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    rows="2"
                  />
                </div>
              </>
            )}

            <button
              type="submit"
              disabled={submitting || !form.paymentMethod}
              className="w-full bg-indigo-600 text-white font-medium py-2 px-4 rounded-lg hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              {submitting ? 'Processing...' : form.paymentMethod === 'CARD' ? 'Pay with Card' : 'Submit Payment'}
            </button>
          </form>

          <p className="text-xs text-gray-500 text-center">Deposit amount is fixed and cannot be changed.</p>
        </div>
      </div>
    </div>
  );
};

export default PublicGraduationDepositPayment;
